import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getUserInfo, getPermissionsForRole } from "../api";
import { PermissionEnum } from "../../../shared/frontend/enums/PermissionEnum";
import { toPermissionValueSet } from "../../../shared/frontend/utils/permissions";

interface UserContextType {
  role: number | undefined;
  dg: string;
  displayName: string;
  isUserInfoLoaded: boolean;
  hasPermission: (permission: PermissionEnum) => boolean;
  setUserEnvironment: (dg: string, displayName?: string) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);
const USER_INFO_REFRESH_INTERVAL_MS = 30_000;

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [role, setRole] = useState<number | undefined>(undefined);
  const [dg, setDg] = useState("INTPA");
  const [displayName, setDisplayName] = useState("INTPA");
  const [isUserInfoLoaded, setIsUserInfoLoaded] = useState(false);
  const [permissionValues, setPermissionValues] = useState<Set<PermissionEnum>>(new Set());
  const permissionRoleRef = useRef<number | undefined>(undefined);
  const environmentRevisionRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    let isRefreshing = false;

    const refreshUserInfo = async () => {
      if (isRefreshing) return;
      isRefreshing = true;
      const environmentRevision = environmentRevisionRef.current;

      try {
        const info = await getUserInfo();
        if (!isMounted) return;

        if (environmentRevision === environmentRevisionRef.current) {
          setDg(info.dg);
          setDisplayName(info.displayName ?? info.dg);
        }
        setRole(info.role);
        setIsUserInfoLoaded(true);

        if (info.role !== permissionRoleRef.current) {
          permissionRoleRef.current = info.role;
          if (typeof info.role === "number") {
            const names = await getPermissionsForRole(info.role);
            if (isMounted && permissionRoleRef.current === info.role) {
              setPermissionValues(toPermissionValueSet(names));
            }
          } else {
            setPermissionValues(new Set());
          }
        }
      } catch (error) {
        console.error("Failed to refresh user info:", error);
      } finally {
        isRefreshing = false;
      }
    };

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshUserInfo();
      }
    };

    void refreshUserInfo();
    const intervalId = window.setInterval(
      () => void refreshUserInfo(),
      USER_INFO_REFRESH_INTERVAL_MS,
    );
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  const setUserEnvironment = useCallback((nextDg: string, nextDisplayName?: string) => {
    environmentRevisionRef.current += 1;
    setDg(nextDg);
    setDisplayName(nextDisplayName ?? nextDg);
  }, []);

  const hasPermission = useCallback(
    (permission: PermissionEnum) => permissionValues.has(permission),
    [permissionValues],
  );

  const value = useMemo(
    () => ({ role, dg, displayName, isUserInfoLoaded, hasPermission, setUserEnvironment }),
    [role, dg, displayName, isUserInfoLoaded, hasPermission, setUserEnvironment],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
